import { useEffect } from 'react';
import { X, MessageSquare, Paperclip, Workflow } from 'lucide-react';
import LogIcon from '../../icons/LogIcon';
import useCardPanelStore from '../../../store/cardPanelStore';
import useBoardStore from '../../../store/boardStore';
import UpdatesTab from './UpdatesTab';
import FilesTab from './FilesTab';
import ActivityTab from './ActivityTab';
import FlowTab from './FlowTab';

export default function CardPanel() {
  const { openCardId, activeTab, setTab, requestCloseCard } = useCardPanelStore();
  const { cards, users, updateCard, cardValues, columns } = useBoardStore();

  const card = cards.find((c) => c.id === openCardId);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && requestCloseCard();
    if (openCardId) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openCardId, requestCloseCard]);

  if (!openCardId || !card) return null;

  const personCol = columns.find((c) => c.type === 'person');
  const personId = personCol ? cardValues[`${card.id}:${personCol.id}`] : null;
  const responsible = users.find((u) => u.id === personId);
  const initials = (n) => n ? n.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '?';

  const handleTitle = (e) => {
    const t = e.target.value.trim();
    if (t && t !== card.title) updateCard(card.id, { title: t });
    else e.target.value = card.title;
  };

  const tabs = [
    { id: 'updates', label: 'Atualizações', icon: MessageSquare },
    { id: 'files', label: 'Arquivos', icon: Paperclip },
    { id: 'activity', label: 'Log de atividade', icon: LogIcon },
    { id: 'flow', label: 'Fluxo', icon: Workflow },
  ];

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={requestCloseCard} />
      <aside className="fixed top-0 right-0 bottom-0 w-[640px] max-w-[92vw] bg-main border-l border-border z-50 flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border/50">
          <button onClick={requestCloseCard} className="p-1 rounded hover:bg-white/5 text-text-secondary" title="Fechar (Esc)">
            <X className="w-5 h-5" />
          </button>
          <input
            defaultValue={card.title}
            onBlur={handleTitle}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className="flex-1 bg-transparent text-xl font-bold text-text-primary outline-none focus:bg-white/5 rounded px-2 py-1"
          />
          {responsible && (
            <div className="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center text-xs font-semibold" title={responsible.name}>
              {responsible.avatar_url ? (
                <img src={responsible.avatar_url} alt={responsible.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                initials(responsible.name)
              )}
            </div>
          )}
        </div>

        <div className="flex items-center border-b border-border/50 px-3">
          {tabs.map((t) => {
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  active
                    ? 'border-accent text-accent'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-hidden">
          {activeTab === 'updates' && <UpdatesTab cardId={card.id} />}
          {activeTab === 'files' && <FilesTab cardId={card.id} />}
          {activeTab === 'activity' && <ActivityTab />}
          {activeTab === 'flow' && <FlowTab />}
        </div>
      </aside>
    </>
  );
}
